import { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { ProductTourProvider } from '../contexts/ProductTourContext';
import { PracticeWalkthroughProvider } from '../contexts/PracticeWalkthroughContext';
import { TeamDashboardWalkthroughProvider } from '../contexts/TeamDashboardWalkthroughContext';
import { AccountMenu } from './AccountMenu';
import { MobileBottomNav } from './MobileBottomNav';
import { MobileMenuDrawer } from './MobileMenuDrawer';
import { ProductTour } from './ProductTour';
import { PracticeWalkthrough } from './PracticeWalkthrough';
import { TeamDashboardWalkthrough } from './TeamDashboardWalkthrough';
import { AkorepsWordmark } from './AkorepsWordmark';
import { OrgUsageBanner } from './OrgUsageBanner';
import { isOrgAdmin, isOrgManager } from '../lib/orgRoles';

export function Layout() {
  const { user, logout } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = isOrgAdmin(user?.orgRole);
  const canViewTeam = isOrgManager(user?.orgRole);
  const isSessionRoute = /^\/practice\/(?!new$)[^/]+/.test(location.pathname);
  const isNewPracticeRoute = location.pathname === '/practice/new';
  const hideDefaultBottomNav = isSessionRoute || isNewPracticeRoute;

  return (
    <ProductTourProvider>
      <PracticeWalkthroughProvider>
        <TeamDashboardWalkthroughProvider>
          <div className="min-h-screen bg-gray-50">
            {!user?.isPlatformAdmin && <OrgUsageBanner />}
            <nav className="bg-white border-b border-gray-200">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-14 md:h-16">
                  <div className="flex items-center gap-8">
                    <Link
                      to="/"
                      className="flex items-center"
                      data-tour="shell-brand"
                      aria-label="Akoreps home"
                    >
                      <AkorepsWordmark className="h-7 w-auto" aria-hidden />
                    </Link>
                    <div className="hidden md:flex items-center gap-6">
                      <Link
                        to="/"
                        className="text-sm text-gray-600 hover:text-gray-900"
                        data-tour="desktop-nav-dashboard"
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/practice"
                        className="text-sm text-gray-600 hover:text-gray-900"
                        data-tour="desktop-nav-practice"
                      >
                        Practice
                      </Link>
                      <Link
                        to="/personas"
                        className="text-sm text-gray-600 hover:text-gray-900"
                        data-tour="desktop-nav-personas"
                      >
                        Personas
                      </Link>
                      {canViewTeam && (
                        <Link
                          to="/team"
                          className="text-sm text-gray-600 hover:text-gray-900"
                          data-tour="desktop-nav-team"
                        >
                          Team
                        </Link>
                      )}
                      {isAdmin && (
                        <Link
                          to="/onboarding"
                          className="text-sm text-gray-600 hover:text-gray-900"
                          data-tour="desktop-nav-business-profile"
                        >
                          Business profile
                        </Link>
                      )}
                      {isAdmin && (
                        <Link
                          to="/goals"
                          className="text-sm text-gray-600 hover:text-gray-900"
                          data-tour="desktop-nav-goals"
                        >
                          Goals
                        </Link>
                      )}
                      {isAdmin && (
                        <Link
                          to="/settings"
                          className="text-sm text-gray-600 hover:text-gray-900"
                          data-tour="desktop-nav-settings"
                        >
                          Settings
                        </Link>
                      )}
                      {isAdmin && (
                        <Link to="/billing" className="text-sm text-gray-600 hover:text-gray-900">
                          Billing
                        </Link>
                      )}
                      {user?.isPlatformAdmin && (
                        <Link
                          to="/admin"
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                          data-tour="desktop-nav-admin"
                        >
                          Admin
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 min-w-0 shrink-0">
                    <AccountMenu onLogout={handleLogout} />
                  </div>
                </div>
              </div>
            </nav>

            <MobileMenuDrawer
              open={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
              user={user}
              isAdmin={isAdmin}
              canViewTeam={canViewTeam}
              onLogout={handleLogout}
            />

            <main
              className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 ${isSessionRoute ? '' : 'pb-24 md:pb-8'}`}
            >
              <Outlet context={{ onOpenMobileMenu: () => setMobileMenuOpen(true) }} />
            </main>

            {!hideDefaultBottomNav && (
              <MobileBottomNav onOpenMenu={() => setMobileMenuOpen(true)} />
            )}
            <ProductTour />
            <PracticeWalkthrough />
            <TeamDashboardWalkthrough />
          </div>
        </TeamDashboardWalkthroughProvider>
      </PracticeWalkthroughProvider>
    </ProductTourProvider>
  );
}
